import clsx from 'clsx';
import {
  Award,
  BarChart3,
  CheckCircle,
  Clock,
  Database,
  Hourglass,
  Layers,
  LayoutDashboard,
  MonitorPlay,
  RefreshCw,
  ShieldCheck,
  Trophy,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../store';

export function DashboardOverview() {
  const {
    stats,
    updateDashboardMetrics,
    showAdminAnalytics,
    showEpisodesManagement,
    showGoldenAdmin,
    showDataProcessAdmin,
    statsCache,
    queueAvailability,
    queueAvailabilityError,
    adminAutoRefresh,
    setAdminAutoRefresh,
  } = useStore();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await updateDashboardMetrics();
    setLastUpdated(new Date());
    setTimeout(() => setIsRefreshing(false), 500); // minimal spinner time
  }, [updateDashboardMetrics]);

  useEffect(() => {
    void handleRefresh();
  }, [handleRefresh]);

  useEffect(() => {
    if (!adminAutoRefresh) return;
    const interval = setInterval(() => {
      void handleRefresh();
    }, 60000);
    return () => clearInterval(interval);
  }, [adminAutoRefresh, handleRefresh]);

  // const operators = useMemo(() => statsCache?.operators ?? [], [statsCache]);
  // const operatorCount = operators.length;
  const totalEpisodes =
    stats.pendingEpisodes +
    stats.votingEpisodes +
    stats.auditPendingEpisodes +
    stats.auditAwaitingConfirmEpisodes +
    stats.auditedEpisodes;
  // annotatedImages (= total_annotated_images) covers every non-pending episode
  // (voting + audit-pending + audited), so the per-episode average must divide
  // by the same set — otherwise excluding voting overestimates it.
  const annotatedEpisodeCount =
    stats.votingEpisodes +
    stats.auditPendingEpisodes +
    stats.auditAwaitingConfirmEpisodes +
    stats.auditedEpisodes;
  const avgFromAnnotated =
    annotatedEpisodeCount > 0 ? Math.round(stats.annotatedImages / annotatedEpisodeCount) : 0;
  // Total frames = all frames in the system (every stage). Estimate: total episodes × avg frames per episode.
  const totalFrames =
    totalEpisodes > 0 && avgFromAnnotated > 0
      ? totalEpisodes * avgFromAnnotated
      : stats.annotatedImages;
  // User formula: avg = total frame / (to annotate*rv + being annotated*1 + to audit*rv + finalized*rv)
  // rv = required_votes per tenant (2 for dual-annotator flows, 1 for single-annotator flows)
  const rv = statsCache?.required_votes ?? 2;
  const auditStageLabel = rv <= 1 ? 'Annotated, needs audit' : 'Both annotated, needs audit';
  const avgDenom =
    stats.pendingEpisodes * rv +
    stats.votingEpisodes * 1 +
    stats.auditPendingEpisodes * rv +
    stats.auditAwaitingConfirmEpisodes * rv +
    stats.auditedEpisodes * rv;
  const avgFramesPerEpisode = avgDenom > 0 ? Math.round(totalFrames / avgDenom) : 0;
  // Total unfinished frames = (to annotate*rv + being annotated*1) * avg frames/episode
  const totalUnfinishedFrames =
    (stats.pendingEpisodes * rv + stats.votingEpisodes * 1) * avgFramesPerEpisode;
  // Total frames to be audit = (total_audited_images / finalized count) * to audit (ratio)
  const totalAuditedImages = statsCache?.total_audited_images ?? 0;
  const availabilityUnavailable = queueAvailabilityError || queueAvailability === null;
  const availabilityValue = (value: number | undefined) =>
    availabilityUnavailable || value == null ? 'Unavailable' : value.toLocaleString();
  const totalFramesToBeAudit =
    stats.auditedEpisodes > 0
      ? Math.round((totalAuditedImages / stats.auditedEpisodes) * stats.auditPendingEpisodes)
      : 0;
  // const pendingShare = totalEpisodes
  //   ? Math.round((stats.pendingEpisodes / totalEpisodes) * 100)
  //   : 0;

  if (statsCache === null) {
    return (
      <div className="flex items-center justify-center py-32 gap-3 text-brand-text-secondary">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header text if needed, or just let the stats speak */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-text-primary">System Overview</h2>
          <p className="text-brand-text-secondary">
            Real-time annotation metrics and quick access.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {lastUpdated && (
            <span className="text-xs text-brand-text-secondary">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <label className="flex items-center gap-2 text-xs text-brand-text-secondary">
            <input
              type="checkbox"
              checked={adminAutoRefresh}
              onChange={(e) => setAdminAutoRefresh(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-brand-border bg-brand-dark text-brand-orange focus:ring-brand-orange"
            />
            Auto refresh
          </label>
          <button
            type="button"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing}
            className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50 disabled:opacity-50"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid: actionable queue stages and completed workflow stages */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* To Annotate: videos not yet annotated */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-brand-text-secondary/30"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-brand-text-secondary">
              <Layers className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              To Annotate
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {stats.pendingEpisodes.toLocaleString()}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">Videos not annotated</div>
            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-brand-border pt-3 text-xs">
              <div>
                <div className="text-brand-text-secondary">For me</div>
                <div className="mt-1 break-words font-mono font-semibold text-emerald-400">
                  {availabilityValue(queueAvailability?.pending.claimable_for_operator)}
                </div>
              </div>
              <div>
                <div className="text-brand-text-secondary">Released</div>
                <div className="mt-1 break-words font-mono font-semibold text-amber-400">
                  {availabilityValue(queueAvailability?.pending.released)}
                </div>
              </div>
              <div>
                <div className="text-brand-text-secondary">Leased</div>
                <div className="mt-1 break-words font-mono font-semibold text-brand-text-primary">
                  {availabilityValue(queueAvailability?.pending.leased_or_delayed)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Available next production vote for the current operator */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-brand-orange/50"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-brand-orange">
              <MonitorPlay className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              Available Next Vote
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {availabilityValue(queueAvailability?.voting.available_next_vote_for_operator)}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">Ready for me</div>
            <div className="mt-3 border-t border-brand-border pt-3 text-xs">
              <div className="text-brand-text-secondary">Global available</div>
              <div className="mt-1 break-words font-mono font-semibold text-brand-orange">
                {availabilityValue(queueAvailability?.voting.available_next_vote_global)}
              </div>
            </div>
          </div>
        </div>

        {/* Full production vote count with an open confirmation window */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-amber-500/50"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-amber-400">
              <Hourglass className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              Awaiting Confirm
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {availabilityValue(queueAvailability?.voting.awaiting_confirm)}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">Votes complete</div>
          </div>
        </div>

        {/* To Audit: two people finished, waiting for audit */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500/50"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-emerald-500">
              <CheckCircle className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              To Audit
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {stats.auditPendingEpisodes.toLocaleString()}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">{auditStageLabel}</div>
          </div>
        </div>

        {/* Finalized: after auditing */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-cyan-500/50"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-cyan-400">
              <Clock className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              Audit Awaiting Confirm
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {stats.auditAwaitingConfirmEpisodes.toLocaleString()}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">Saved audit drafts</div>
          </div>
        </div>

        {/* Finalized: after auditing */}
        <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-surface p-6 transition-all hover:border-brand-border/80 hover:shadow-lg">
          <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500/50"></div>
          <div className="flex items-center justify-between">
            <div className="rounded-lg bg-brand-dark p-2 text-indigo-500">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-brand-text-secondary">
              Finalized
            </span>
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-brand-text-primary font-mono">
              {stats.auditedEpisodes.toLocaleString()}
            </div>
            <div className="text-sm text-brand-text-secondary mt-1">Audit complete</div>
          </div>
        </div>
      </div>

      {/* Insights: Total Frames, Avg Frames/Episode, Total unfinished frames, Total frames to be audit */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="flex items-center gap-2 text-brand-text-secondary text-xs font-semibold uppercase tracking-wider">
            <BarChart3 className="h-4 w-4 text-white" />
            Total Frames
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalFrames.toLocaleString()}
          </div>
          <div className="text-xs text-brand-text-secondary mt-1">
            All stages {avgFramesPerEpisode > 0 ? `· ${avgFramesPerEpisode} avg/episode` : ''}
          </div>
        </div>

        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Avg Frames / Episode
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {avgFramesPerEpisode.toLocaleString()}
          </div>
        </div>

        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            PENDING ANNOTATION
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalUnfinishedFrames.toLocaleString()}
          </div>
          <div className="text-sm text-brand-text-secondary mt-1">
            To annotate + being annotated
          </div>
        </div>

        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            PENDING AUDIT
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {totalFramesToBeAudit.toLocaleString()}
          </div>
          <div className="text-sm text-brand-text-secondary mt-1">Estimated frames to audit</div>
        </div>

        {/* Unused: Active Operators, Pending Share */}
        {/* <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="flex items-center gap-2 text-brand-text-secondary text-xs font-semibold uppercase tracking-wider">
            <Users className="h-4 w-4 text-brand-orange" />
            Active Operators
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {operatorCount.toLocaleString()}
          </div>
        </div>

        <div className="rounded-xl border border-brand-border bg-brand-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-text-secondary">
            Pending Share
          </div>
          <div className="mt-2 text-2xl font-bold text-brand-text-primary font-mono">
            {pendingShare}%
          </div>
        </div> */}
      </div>

      {/* Navigation Grid */}
      <div>
        <h3 className="text-lg font-semibold text-brand-text-primary mb-4">Quick Actions</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <button
            type="button"
            onClick={() => void useStore.getState().showOperatorDashboard()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-brand-orange hover:bg-brand-surface-highlight"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-dark text-white group-hover:scale-110 transition-transform">
              <LayoutDashboard className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                My Dashboard
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">View my history</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => void showAdminAnalytics()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-brand-orange hover:bg-brand-surface-highlight"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-dark text-white group-hover:scale-110 transition-transform">
              <Trophy className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                Leaderboard
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">View operator rankings</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => void showEpisodesManagement()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-brand-orange hover:bg-brand-surface-highlight"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-dark text-brand-text-secondary group-hover:text-white group-hover:scale-110 transition-transform">
              <Database className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                Episodes
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">Manage annotation queues</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => void showDataProcessAdmin()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-emerald-500/60 hover:bg-emerald-500/5"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-300 group-hover:scale-110 transition-transform">
              <Layers className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                Data Process
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">
                Priority upload · queue reset
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => void useStore.getState().showUserSelection()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-brand-orange hover:bg-brand-surface-highlight"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-dark text-white group-hover:scale-110 transition-transform">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                Impersonate User
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">Switch operator view</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => void showGoldenAdmin()}
            className="group flex flex-col gap-4 rounded-xl border border-brand-border bg-brand-surface p-5 text-left transition-all hover:border-amber-500/60 hover:bg-amber-500/5"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-300 group-hover:scale-110 transition-transform">
              <Award className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-semibold text-brand-text-primary group-hover:text-white">
                Golden Set
              </h4>
              <p className="text-sm text-brand-text-secondary mt-1">
                Hidden calibration · admin-curated ground truth
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
